import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listPaymentMethods } from '../services/subscriptionService';
import PaymentMethodsList from '../components/subscription/PaymentMethodsList';
import PaymentMethodForm from '../components/subscription/PaymentMethodForm';
import { Loader2, ShieldAlert } from 'lucide-react'; // Added ShieldAlert for error
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function PaymentMethodManagementPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // This state is for the PaymentMethodsList component if it needs to highlight a selection.
  // Or if actions on this page are tied to a specific selection.
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);

  const { data: paymentMethods, isLoading: isLoadingPaymentMethods, error: paymentMethodsError } = useQuery({
    queryKey: ['paymentMethods'], // This query will be shared with other places using paymentMethods
    queryFn: listPaymentMethods,
  });

  const handlePaymentMethodAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    toast.success(t('newPaymentMethodAdded'));
    // Optionally, clear selection or select the new method
    // setSelectedPaymentMethodId(newlyAddedMethodId);
  };

  const handlePaymentMethodError = (error: Error) => {
     toast.error(t('errorAddingPaymentMethod', { error: error.message }));
  };

  if (isLoadingPaymentMethods) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-4 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
        <p className="ml-3 text-xl text-gray-700 mt-4">{t('loadingPaymentMethods')}</p>
      </div>
    );
  }

  if (paymentMethodsError) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-4 text-center text-red-600">
        <ShieldAlert className="h-12 w-12 mb-4" />
        <p className="text-xl font-semibold">{t('errorLoadingPaymentMethods')}</p>
        <p className="text-md">{paymentMethodsError.message}</p>
        <p className="mt-4 text-sm text-gray-500">{t('tryAgainLater')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-8 text-center">{t('paymentMethodManagement')}</h1>

        <div className="bg-white shadow-xl rounded-lg p-6 mb-10">
          <PaymentMethodsList
              paymentMethods={paymentMethods || []}
              // isLoading prop might be useful if PMList has its own specific loading indicators for actions
              // For now, page-level loading is primary.
              isLoading={isLoadingPaymentMethods}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onSelectPaymentMethod={setSelectedPaymentMethodId}
              // Assuming PaymentMethodsList might have props like onDelete, onSetDefault
              // which would trigger mutations and then invalidate queryClient.invalidateQueries(['paymentMethods'])
              // These would need to be implemented in PaymentMethodsList and corresponding service functions.
          />
          {(!paymentMethods || paymentMethods.length === 0) && !isLoadingPaymentMethods && (
            <p className="text-center text-gray-500 py-4">{t('noSavedPaymentMethods')}</p>
          )}
        </div>

        <div className="bg-white shadow-xl rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('addNewPaymentMethod')}</h2>
          <PaymentMethodForm
            onSuccess={handlePaymentMethodAdded}
            onError={handlePaymentMethodError}
            // If PaymentMethodForm needs any specific context like a user ID (though likely handled by http-only cookie)
          />
        </div>
      </div>
    </div>
  );
}
